import * as React from 'react';
import { PrimaryButton } from '@gje/buttons/lib/primary-button';
import { getText } from '../../../services/text/text-service';
import { Title } from '@gje/typography/lib/title';
import { KeyboardArrowDown } from '@gje/icons/lib/keyboard-arrow-down';
import HelpBox from '../HelpBox/HelpBox';
import posed from 'react-pose';
import { resolvePaymentTerm } from '../../../services/calculation-data/calculation-data-service';
import { Text } from '@gje/typography/lib';
import classNames from 'classnames';
import { numberWithSpaces } from '../../../utils/stringUtils';
import {
    isOkq8,
    isVillaagarna
} from '../../../services/partners/partnersService';
import { HtmlPanel } from '../HtmlPanel/HtmlPanel';

const selectedCardImage = require('../../../images/selectedCard.png');
const selectedCardImageOK = require('../../../images/selectedCardOK.png');
import { getPriceWithoutDiscount } from '../../../services/customer/customer-service';

interface CardsProps {
    insuranceData: any;
    isOpen: boolean;
    handleClick: Function;
    price: number | string;
    paymentTerm: number;
    recommended: boolean;
    recommendationText: string;
    mobileFontSize?: number;
    isAgeLimitExceededForFullInsurance?: boolean;
    campaignDiscountPercentage?: number;
}

const Card = posed.div({
    closed: {
        height: isOkq8() ? 230 : 205,
        backgroundColor: isOkq8() ? '#FFFFFF' : '#f0ece6',
        transition: {
            delay: 100,
            ease: 'easeIn',
            duration: 300
        }
    },
    open: {
        height: isOkq8() ? 230 : 205,
        backgroundColor: isVillaagarna()
            ? '#EEF9EA'
            : isOkq8()
            ? '#FFFFFF'
            : '#dfe9e1',
        transition: {
            delay: 100,
            ease: 'easeIn',
            duration: 300
        }
    }
});

const ButtonContainer = posed.div({
    closed: {
        delay: 500,
        opacity: 1
    },
    open: {
        opacity: 0,
        zIndex: -1
    }
});

const MobileExpandableCard: React.FunctionComponent<CardsProps> = ({
    handleClick,
    paymentTerm,
    isOpen,
    recommended,
    insuranceData,
    price,
    recommendationText,
    mobileFontSize,
    isAgeLimitExceededForFullInsurance,
    campaignDiscountPercentage
}) => {
    const priceTooHigh = typeof price === 'string';

    return (
        <Card
            pose={isOpen ? 'open' : 'closed'}
            className={'mobile-expandable-card collapsed-card'}>
            {isOpen && (
                <div className="mobile-selected-card-image-container">
                    <img
                        className="mobile-selected-card-image"
                        src={isOkq8() ? selectedCardImageOK : selectedCardImage}
                        alt="selected-insurance-img"
                    />
                </div>
            )}
            <div className="mobile-card-content">
                <Text
                    size={'h4'}
                    className={classNames(
                        'line-height-1-1',
                        'word-break-word',
                        `margin-bottom-${priceTooHigh ? '6-5' : '6-5'}`,
                        !insuranceData.mobileCardName &&
                            recommended &&
                            !isOkq8()
                            ? 'display-inline-flex align-items'
                            : '',
                        mobileFontSize && `font-size-${mobileFontSize}`,
                        { 'okq8-grey-label': isOkq8() }
                    )}>
                    <HtmlPanel>
                        {insuranceData.mobileCardName
                            ? insuranceData.mobileCardName
                            : insuranceData.name}
                    </HtmlPanel>
                    {recommended && (
                        <HelpBox
                            helpText={recommendationText}
                            place={
                                insuranceData.name === 'Trafik' ||
                                insuranceData.name === 'Trafikförsäkring'
                                    ? 'right'
                                    : insuranceData.name === 'Halv' ||
                                      insuranceData.name === 'Halvförsäkring'
                                    ? 'bottom'
                                    : 'left'
                            }
                            id={insuranceData.name + '-helpbox'}
                            isMobile={true}
                            useGreen={!isOkq8()}
                            useBlue={isOkq8()}
                            iconClassName={classNames(
                                'smaller-mobile-help-icon negative-margin-top-4',
                                { 'visibility-hidden': !recommended }
                            )}
                        />
                    )}
                </Text>
                {!priceTooHigh && (
                    <>
                        {campaignDiscountPercentage > 0 && (
                            <Title
                                tag={'h4'}
                                className={classNames(
                                    'line-height-1-1',
                                    'cross-mark',
                                    'small-screen',
                                    {
                                        'okq8-font-bold okq8-blue-color font-size-25': isOkq8()
                                    }
                                )}>
                                {numberWithSpaces(
                                    getPriceWithoutDiscount(
                                        Number(price),
                                        Number(campaignDiscountPercentage)
                                    )
                                )}
                            </Title>
                        )}
                        <Title
                            tag={'h3'}
                            className={classNames('line-height-1-1', {
                                'okq8-font-bold okq8-blue-color font-size-32': isOkq8()
                            })}>
                            {numberWithSpaces(Number(price))}
                        </Title>
                    </>
                )}
            </div>
            {priceTooHigh && (
                <Text
                    className={classNames('mobile-contact-us-text', {
                        'okq8-font-bold okq8-blue-color': isOkq8()
                    })}>
                    {price}
                </Text>
            )}
            <Text
                className={classNames({ 'okq8-grey-label': isOkq8() })}
                size={'h4'}>
                {resolvePaymentTerm(paymentTerm, price)}
            </Text>
            <ButtonContainer
                pose={isOpen ? 'open' : 'closed'}
                hidden={isOpen || isAgeLimitExceededForFullInsurance}
                className={classNames({ 'background-white': isOkq8() })}>
                <PrimaryButton
                    borderColor={'red'}
                    flexible={true}
                    id={`selector-radio-${insuranceData.name}`}
                    name={insuranceData.name}
                    onClick={e => handleClick(e)}
                    className={classNames(
                        `position-bottom z-index-${isOpen ? 'below' : 'above'}`,
                        { 'mobile-button-padding': !isOkq8() },
                        'primary-button-border-color',
                        isVillaagarna()
                            ? 'villaagarna-green-bg-color no-border villaagarna-button'
                            : '',
                        isOkq8()
                            ? 'okq8-blue-bg-color no-border okq8-small-button margin-bottom-26'
                            : ''
                    )}>
                    <div className={'display-inline-flex align-items'}>
                        <Title
                            tag={'h4'}
                            children={getText(
                                'webshopsweden.common.cardButton'
                            )}
                        />
                        <KeyboardArrowDown
                            color={isVillaagarna() ? '#ffffff' : ''}
                            size={24}
                        />
                    </div>
                </PrimaryButton>
            </ButtonContainer>
        </Card>
    );
};

export default MobileExpandableCard;
